import React from 'react';
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { tokens, withAlpha } from '../../Common/designTokens';

const ConfirmDeleteDialog = ({ open, onClose, onConfirm, type, rowData, isLoading, canWrite = false }) => {
  const writeAccessDeniedMessage = 'Only SUPER_ADMIN can modify session and user configuration.';

  const getDisplayInfo = () => {
    if (!rowData) return { title: '', details: [] };

    if (type === 'session') {
      return {
        title: 'Confirm Delete Session Configuration',
        details: [
          { label: 'Customer Name', value: rowData.customerName || 'Unknown' },
          { label: 'Venue Name', value: rowData.venueName || 'Unknown' },
          { label: 'Sender Comp ID', value: rowData.senderCompId || 'Unknown' },
          { label: 'Target Comp ID', value: rowData.targetCompId || 'Unknown' },
          { label: 'Connection Type', value: rowData.connectionType || 'Unknown' },
        ]
      };
    } else {
      return {
        title: 'Confirm Delete User Configuration',
        details: [
          { label: 'User ID', value: rowData.userId || 'Unknown' },
          { label: 'Venue User', value: rowData.venueUser || 'Unknown' },
          { label: 'Venue ID', value: rowData.venueId || 'Unknown' },
        ]
      };
    }
  };

  const handleConfirmDelete = async () => {
    if (!rowData) return;
    if (!canWrite) return;
    await onConfirm(rowData);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!rowData) return null;

  const { title, details } = getDisplayInfo();

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  return (
    <Modal
      open={open}
      onClose={!isLoading ? handleClose : undefined}
      aria-labelledby="confirm-delete-modal"
    >
      <Box sx={modalStyle}>
        <Typography 
          id="confirm-delete-modal-title" 
          variant="h6" 
          component="h2" 
          sx={{ 
            color: tokens.colors.fontColour, 
            mb: 2, 
            textAlign: 'center', 
            fontWeight: 'bold' 
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: tokens.colors.fontColour85, 
            textAlign: 'center', 
            mb: 2 
          }}
        >
          Are you sure you want to delete this {type === 'session' ? 'session' : 'user'} configuration?
        </Typography>

        {details.map((detail, index) => (
          <Typography 
            key={index}
            variant="body2" 
            sx={{ 
              mb: 1, 
              color: tokens.colors.fontColour, 
              textAlign: 'center' 
            }}
          >
            <strong>{detail.label}:</strong> {detail.value}
          </Typography>
        ))}

        <Typography 
          variant="body2" 
          sx={{ 
            color: tokens.palette.danger, 
            mb: 3, 
            mt: 1,
            textAlign: 'center', 
            fontWeight: 600 
          }}
        >
          This action cannot be undone.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 120,
              borderRadius: '8px',
              '&.Mui-disabled': {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmDelete}
            disabled={isLoading || !canWrite}
            title={!canWrite ? writeAccessDeniedMessage : undefined}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 140,
              color: tokens.colors.white,
              borderColor: tokens.palette.danger,
              backgroundColor: withAlpha(tokens.palette.danger, 0.95),
              borderRadius: '8px',
              '&:hover': { backgroundColor: withAlpha(tokens.palette.danger, 1) },
              '&.Mui-disabled': {
                color: withAlpha(tokens.colors.white, 0.4),
                borderColor: withAlpha(tokens.colors.white, 0.4),
              }
            }}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmDeleteDialog;
